import { useContext } from 'react'
import { ReadGridHeader } from './ReadGridHeader'
import { ReadGridRowNumCell } from './ReadGridRowNumCell'
import { ReadGridRowSelectCell } from './ReadGridRowSelectCell'
import { ReadGridSelectAllCell } from './ReadGridSelectAllCell'
import { ReadGridDataCell } from './readGridDataCell'
import { ReadGridColumnWidthContext } from '../context/column-width-context'

/**
 * Render the cell based on type of cell.
 * @param props
 * @param {{ fieldId: number }} copyableColumns An object of field IDs, for values that can be copied to the clipboard.
 */
export const CellRenderer = ({
  selectedCells,
  columnIndex,
  rowIndex,
  style,
  parent,
  copyableColumns,
  userOptions,
  selectAll,
  creativeClassicationsEnabled = false,
  chipsListEnabled = false,
  imagePreviewEnabled = false,
}) => {
  const columnWidthContext = useContext(ReadGridColumnWidthContext)
  const { data, columns, selectedRows } = parent.props
  const column = columns[columnIndex]
  const row = data[rowIndex]
  const isHeader = rowIndex === 0
  const isRowNumColumn = columnIndex === 0
  const isSelectAllColumn = isHeader && isRowNumColumn // top left cell
  const cellWidth = columnWidthContext.getColumnWidth(columnIndex)
  const updateStyle = { ...style, width: cellWidth }
  const isMultiSelectColumn = parent.props.multiSelect && columnIndex === 1
  const isHeaderSelected = columnIndex === 1 && isHeader && selectAll
  const isCheckboxDisabled = !selectedRows[rowIndex] && selectAll
  const { isLastColumn } = column

  /**
   * If this is the last column, return an empty div, because this column
   * will act just as a spacer.
   */
  if (isLastColumn) {
    return <div />
  }

  if (isSelectAllColumn) {
    return (
      <ReadGridSelectAllCell
        column={column}
        row={row}
        data={data}
        columnIndex={columnIndex}
        parent={parent}
        rowIndex={rowIndex}
        style={updateStyle}
      />
    )
  }

  if (isRowNumColumn) {
    return (
      <ReadGridRowNumCell
        column={column}
        row={row}
        data={data}
        columnIndex={columnIndex}
        parent={parent}
        rowIndex={rowIndex}
        style={updateStyle}
      />
    )
  }

  if (isMultiSelectColumn) {
    return (
      <ReadGridRowSelectCell
        column={column}
        row={row}
        data={data}
        columnIndex={columnIndex}
        parent={parent}
        rowIndex={rowIndex}
        style={updateStyle}
        isDisabled={row?.isDisabled}
        isCheckboxDisabled={isCheckboxDisabled}
        isHeaderSelected={isHeaderSelected}
      />
    )
  }

  if (isHeader) {
    return (
      <ReadGridHeader
        column={column}
        row={row}
        columnIndex={columnIndex}
        parent={parent}
        style={updateStyle}
        userOptions={userOptions}
      />
    )
  }

  // default to the standard data cell
  return (
    <ReadGridDataCell
      column={column}
      columnIndex={columnIndex}
      columns={copyableColumns}
      parent={parent}
      row={row}
      rowIndex={rowIndex}
      selectedCells={selectedCells}
      style={updateStyle}
      isCheckboxDisabled={isCheckboxDisabled}
      creativeClassicationsEnabled={creativeClassicationsEnabled}
      chipsListEnabled={chipsListEnabled}
      imagePreviewEnabled={imagePreviewEnabled}
    />
  )
}
